import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

export default function SEO({ 
  title, 
  description, 
  keywords = [], 
  ogImage = '/thromBioInvert.png', 
  ogType = 'website',
  canonicalUrl,
  schema,
  clinicalTrial = false,
  drugInformation = false,
  researchPublication = false
}) {
  const location = useLocation();
  
  const fullTitle = title ? `${title} | ThromBio` : 'Revolutionizing Stroke Care | ThromBio';
  
  const metaDescription = description || 'Building on 30 years of world-leading research in blood clotting and stroke, TBO-309 shows promising Phase II data, potentially revolutionizing stroke care worldwide. Learn more.';
  
  const metaKeywords = [
    'ThromBio', 
    'Stroke Treatment', 
    'Anti-thrombotic', 
    'TBO-309', 
    'Founded by Professor Shaun Jackson',
    'Ischemic stroke treatment',
    'Antiplatelet therapy',
    'Thrombolytic therapy',
    'Blood clot prevention',
    'Acute stroke intervention',
    ...keywords
  ].join(', ');

  const baseUrl = 'https://www.thrombio.com.au';
  const currentCanonicalUrl = canonicalUrl || `${baseUrl}${location.pathname}`;
  
  const absoluteOgImage = ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`;

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "ThromBio",
    "alternateName": "ThromBio Holdings Pty Ltd",
    "url": "https://www.thrombio.com.au/",
    "logo": "https://www.thrombio.com.au/thromBioInvert.png",
    "foundingDate": "2015",
    "description": "ThromBio is a drug discovery company dedicated to advancing the global standard of care for the treatment of stroke using novel anti-thrombotic drugs with unprecedented safety and efficacy.",
    "medicalSpecialty": [
      "Stroke Treatment",
      "Antithrombotic Therapy", 
      "Cardiovascular Medicine",
      "Clinical Research",
      "Drug Development"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "info@thrombio.com.au"
    },
    "sameAs": [
      "https://www.linkedin.com/company/thrombioholdings"
    ]
  };

  const clinicalTrialSchema = clinicalTrial ? {
    "@context": "https://schema.org",
    "@type": "MedicalStudy",
    "name": title,
    "description": metaDescription,
    "sponsor": {
      "@type": "Organization",
      "name": "ThromBio Holdings Pty Ltd"
    },
    "studySubject": {
      "@type": "MedicalCondition",
      "name": "Acute Ischemic Stroke"
    },
    "phase": "Phase II",
    "status": "Recruiting"
  } : null;

  const drugSchema = drugInformation ? {
    "@context": "https://schema.org",
    "@type": "Drug",
    "name": "TBO-309",
    "description": "Novel antiplatelet compound currently in Phase II clinical trials for the treatment of acute ischemic stroke",
    "activeIngredient": "PI3Kβ inhibitor",
    "mechanismOfAction": "Phosphoinositide 3-kinase beta inhibition",
    "clinicalPharmacology": "Antiplatelet therapy for acute ischemic stroke",
    "manufacturer": {
      "@type": "Organization",
      "name": "ThromBio Holdings Pty Ltd"
    }
  } : null;

  const publicationSchema = researchPublication ? {
    "@context": "https://schema.org",
    "@type": "ScholarlyArticle",
    "headline": title,
    "description": metaDescription,
    "author": {
      "@type": "Person",
      "name": "Professor Shaun Jackson"
    },
    "publisher": {
      "@type": "Organization",
      "name": "ThromBio"
    },
    "about": {
      "@type": "MedicalCondition",
      "name": "Stroke"
    }
  } : null;

  const defaultSchema = schema || organizationSchema;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      
      <link rel="canonical" href={currentCanonicalUrl} />
      
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentCanonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={absoluteOgImage} />
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={absoluteOgImage} />

      {/* Main schema - always render either custom or default */}
      <script type="application/ld+json">
        {JSON.stringify(defaultSchema)}
      </script>

      {/* Additional schemas based on page type */}
      {clinicalTrialSchema && (
        <script type="application/ld+json">
          {JSON.stringify(clinicalTrialSchema)}
        </script>
      )}

      {drugSchema && (
        <script type="application/ld+json">
          {JSON.stringify(drugSchema)}
        </script>
      )}

      {publicationSchema && (
        <script type="application/ld+json">
          {JSON.stringify(publicationSchema)}
        </script>
      )}
    </Helmet>
  );
}